import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { respGridUtil } from '@marriott/mi-headless-utils';
import { toRem } from '@marriott/mi-ui-library';
import { StyledSkeletonLoaderDiv, StyledSkeletonLoaderFormContainer } from './NGSearchFormABTestWrapper.styles';

/**
 * Fallback top offset values when DOM header is not available (SSR, or elements not yet rendered).
 * Matches StickyComponentWrapper BAU variant.
 */
const NON_STICKY_TOP_FALLBACK_BY_BREAKPOINT = {
  xs: { HTLHeader: 112, globalNav: 52 },
  sm: { HTLHeader: 112, globalNav: 52 },
  md: { HTLHeader: 112, globalNav: 52 },
  lg: { HTLHeader: 112, globalNav: 88 },
} as const;

const getElementHeight = (el: Element | null): number => {
  if (!el) return 0;
  if (typeof (el as Element & { getBoundingClientRect?: () => DOMRect }).getBoundingClientRect === 'function') {
    return (el.getBoundingClientRect().height ?? 0) || 0;
  }
  const htmlEl = el as HTMLElement;
  return htmlEl?.offsetHeight ?? htmlEl?.clientHeight ?? 0;
};

/**
 * Dynamically measure header height from DOM.
 * - Global nav: [id="navcontainer"]
 * - HTL header: .m-header + .ab (general message banner when present)
 */
const getHeaderHeightFromDOM = (hasGlobalNav: boolean): number | null => {
  if (typeof window === 'undefined' || !window.document) return null;

  const banner = document.querySelector('.ab');
  const bannerHeight = banner ? getElementHeight(banner) : 0;
  if (hasGlobalNav) {
    const globalNav = document.querySelector('[id="navcontainer"]');
    const height = getElementHeight(globalNav);
    const total = height + bannerHeight;
    return total > 0 ? total : null;
  }

  const htlHeader = document.querySelector('.m-header');
  const headerHeight = getElementHeight(htlHeader);
  const total = headerHeight + bannerHeight;
  return total > 0 ? total : null;
};

const getNonStickyTop = (breakpoint: 'xs' | 'sm' | 'md' | 'lg', hasGlobalNav: boolean): number => {
  const measured = getHeaderHeightFromDOM(hasGlobalNav);
  if (measured !== null) return measured;
  const headerType = hasGlobalNav ? 'globalNav' : 'HTLHeader';
  return NON_STICKY_TOP_FALLBACK_BY_BREAKPOINT[breakpoint][headerType];
};

// Types matching IMerchandisingDCAContainerProps
interface INGSearchformABTestWrapperProps {
  pagePath: string;
  itemPath: string;
  isAuthorMode: boolean;
  isAuthor: boolean;
  isTargetContentLoaded: boolean;
  cqItems: Record<string, unknown>;
  cqItemsOrder: string[];
  enableOfferVariation: boolean;
  styleclass: string;
  displayEvergreenContent: boolean;
  hideByDefault?: boolean;
  mboxParameter?: string;
  // Additional props that may be passed through
  allowedComponents?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface IUpdatedPageModel extends Record<string, unknown> {
  alloyModelJSON: Record<string, Record<string, unknown>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mboxData?: Array<{
    mboxName: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uxlResponse?: {
      isTargetContentError?: boolean;
      metaData?: {
        experience?: string;
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }>;
}

/**
 * Removes search form items from cqItems and cqItemsOrder
 * Only affects search form components, leaving all other components intact
 */
const removeSearchFormFromItems = (
  cqItems: Record<string, unknown>,
  cqItemsOrder: string[],
  searchformToRemove: string
): { updatedCqItems: Record<string, unknown>; updatedCqItemsOrder: string[] } => {
  const updatedCqItems = { ...cqItems };
  const updatedCqItemsOrder = [...cqItemsOrder];

  // Iterate through each item in cqItems
  Object.keys(updatedCqItems).forEach(itemKey => {
    const item = updatedCqItems[itemKey] as Record<string, unknown> | undefined;

    if (item && typeof item === 'object') {
      // Check if this item has :items (it's a container)
      const items = item[':items'] as Record<string, unknown> | undefined;

      if (items && typeof items === 'object') {
        // Check if any of the items in this container is the search form we want to remove
        const itemsToRemove: string[] = [];

        Object.keys(items).forEach(subItemKey => {
          const subItem = items[subItemKey] as Record<string, unknown> | undefined;
          if (subItem && typeof subItem === 'object') {
            const itemType = subItem[':type'] as string | undefined;
            // Check if this sub-item matches the search form we want to remove
            if (itemType && itemType.includes(searchformToRemove)) {
              itemsToRemove.push(subItemKey);
            }
          }
        });

        // Remove the search form items from the container
        if (itemsToRemove.length > 0) {
          const updatedItems = { ...items };
          itemsToRemove.forEach(key => {
            delete updatedItems[key];
          });

          // Update the item with the modified items
          updatedCqItems[itemKey] = {
            ...item,
            ':items': updatedItems,
          };
        }
      }
    }
  });

  return { updatedCqItems, updatedCqItemsOrder };
};

/**
 * Processes target-nextGenSearch-mbox mbox parameter to determine which search form to remove
 * based on the DCA target response
 */
const processPhoenixSearchExperience = (
  updatedPageModel: IUpdatedPageModel | undefined,
  mboxParameter: string,
  props: INGSearchformABTestWrapperProps,
  isFallbackScenario: boolean
): INGSearchformABTestWrapperProps | null => {
  let isProcessingComplete = false;
  let searchFormExperience: string | undefined;
  let hasError = false;

  if (isFallbackScenario) {
    searchFormExperience = 'bsf';
    isProcessingComplete = true;
  } else {
    // Check mboxData for processing completion
    if (updatedPageModel?.mboxData) {
      for (let componentCount = 0; componentCount < updatedPageModel.mboxData.length; componentCount++) {
        const component = updatedPageModel.mboxData[componentCount];
        if (component.mboxName === mboxParameter) {
          // Check if processing is complete (has response or error)
          if (component?.uxlResponse) {
            isProcessingComplete = true;
            if (component?.uxlResponse?.isTargetContentError === true) {
              hasError = true;
            } else {
              searchFormExperience = component?.uxlResponse?.metaData?.experience;
            }
          }
          break;
        }
      }
    }
  }

  // Return null if processing is not complete yet
  if (!isProcessingComplete) {
    return null;
  }

  // Determine which search form to remove based on response
  let searchformToRemove: string | null = null;
  if (hasError || !searchFormExperience || (searchFormExperience !== 'bsf' && searchFormExperience !== 'ngsf')) {
    // Fallback: keep baselinesearchform, remove ngsearchform
    searchformToRemove = 'ngsearchform';
  } else if (searchFormExperience === 'bsf') {
    // Experience is baseline search form - remove ngsearchform
    searchformToRemove = 'ngsearchform';
  } else if (searchFormExperience === 'ngsf') {
    // Experience is next gen search form - remove baselinesearchform
    searchformToRemove = 'baselinesearchform';
  }

  // Apply search form removal if needed
  if (
    searchformToRemove &&
    props.cqItems &&
    Object.keys(props.cqItems).length > 0 &&
    props.cqItemsOrder &&
    props.cqItemsOrder.length > 0
  ) {
    const { updatedCqItems, updatedCqItemsOrder } = removeSearchFormFromItems(
      props.cqItems,
      props.cqItemsOrder,
      searchformToRemove
    );

    return {
      ...props,
      cqItems: updatedCqItems,
      cqItemsOrder: updatedCqItemsOrder,
      isTargetContentLoaded: true,
      searchFormExperience,
    };
  }

  // No removal needed, but mark as processed
  return {
    ...props,
    isTargetContentLoaded: true,
    searchFormExperience,
  };
};

const DCAContainer: FC<INGSearchformABTestWrapperProps> = props => {
  const {
    isAuthorMode,
    pagePath,
    itemPath,
    cqItemsOrder,
    cqItems,
    isTargetContentLoaded,
    enableOfferVariation = false,
  } = props;

  const skeletonLoaderRef = useRef<HTMLDivElement>(null);
  const [topPosition, setTopPosition] = useState<number>(0);
  const [isSkeletonSticky, setIsSkeletonSticky] = useState<boolean>(false);
  const [topOffsetsByBreakpoint, setTopOffsetsByBreakpoint] = useState<{
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  }>({});
  const updatedPageModel = useDCAPageModelStore(state => state.pageModel) as IUpdatedPageModel | undefined;

  // Helper function to check if a container component contains search form components
  // and if search form should be positioned on herobanner
  const containerHasSearchForm = (
    container: Record<string, unknown>
  ): { hasSearchForm: boolean; enableOverlayTop: boolean } => {
    const items = container?.[':items'] as Record<string, unknown> | undefined;
    if (!items) {
      return { hasSearchForm: false, enableOverlayTop: false };
    }

    let hasSearchForm = false;
    let enableOverlayTop = false;

    // Check if any item in the container is a search form component
    Object.values(items).forEach((item: unknown) => {
      const itemObj = item as Record<string, unknown> | undefined;
      const itemType = itemObj?.[':type'] as string | undefined;
      if (
        itemType?.includes('baselinesearchform') ||
        itemType?.includes('ngsearchform') ||
        itemType?.includes('homepagesearch')
      ) {
        hasSearchForm = true;
        // Check if search form should be positioned on herobanner (enableOverlayTop)
        const overlayTop = itemObj?.['enableOverlayTop'] as boolean | undefined;
        if (overlayTop) {
          enableOverlayTop = true;
        }
      }
    });
    return { hasSearchForm, enableOverlayTop };
  };

  // Detect hasGlobalNav (matches import-components.js - same logic as StickyComponentWrapper)
  const detectHasGlobalNav = useCallback((): boolean => {
    const getResponsivegridItems = (model: Record<string, unknown> | undefined) => {
      const cqItems = model?.['cqItems'] as Record<string, unknown> | undefined;
      const root = cqItems?.['root'] as Record<string, unknown> | undefined;
      const rootItems = root?.[':items'] as Record<string, unknown> | undefined;
      const responsivegrid = rootItems?.['responsivegrid'] as Record<string, unknown> | undefined;
      return responsivegrid?.[':items'] as Record<string, unknown> | undefined;
    };
    const items = getResponsivegridItems(updatedPageModel as Record<string, unknown> | undefined);
    if (items && Object.values(items).some((c: unknown) => (c as Record<string, unknown>)?.['id'] === 'navcontainer'))
      return true;
    if (typeof window !== 'undefined') {
      const windowModel = (window as unknown as { pageProps?: { model?: Record<string, unknown> } })?.pageProps
        ?.model as Record<string, unknown> | undefined;
      const wItems = getResponsivegridItems(windowModel);
      if (
        wItems &&
        Object.values(wItems).some((c: unknown) => (c as Record<string, unknown>)?.['id'] === 'navcontainer')
      )
        return true;
      return !!document.querySelector('[id="navcontainer"]');
    }
    return false;
  }, [updatedPageModel]);

  // Get current breakpoint (xs < 576, sm >= 576, md >= 768, lg >= 992)
  const getCurrentBreakpoint = useCallback((): 'xs' | 'sm' | 'md' | 'lg' => {
    if (typeof window === 'undefined') return 'xs';
    const width = window.innerWidth;
    if (width >= 992) return 'lg';
    if (width >= 768) return 'md';
    if (width >= 576) return 'sm';
    return 'xs';
  }, []);

  // Calculate top position - uses StickyComponentWrapper logic for same position as ngsearchform
  const calculateTopPosition = useCallback((): number => {
    const breakpoint = typeof window !== 'undefined' ? getCurrentBreakpoint() : 'xs';
    const hasGlobalNav = detectHasGlobalNav();
    return getNonStickyTop(breakpoint, hasGlobalNav);
  }, [getCurrentBreakpoint, detectHasGlobalNav]);

  // Top positions for all breakpoints (for CSS media query responsiveness)
  const calculateTopPositionsByBreakpoint = useCallback((): {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  } => {
    const hasGlobalNav = detectHasGlobalNav();
    return {
      xs: getNonStickyTop('xs', hasGlobalNav),
      sm: getNonStickyTop('sm', hasGlobalNav),
      md: getNonStickyTop('md', hasGlobalNav),
      lg: getNonStickyTop('lg', hasGlobalNav),
    };
  }, [detectHasGlobalNav]);

  // Update top position when component mounts and on resize (matches StickyComponentWrapper breakpoints)
  // Skip in author mode - no skeleton/position styles needed
  useEffect(() => {
    if (isAuthorMode) return;

    const updatePosition = () => {
      const calculatedTop = calculateTopPosition();
      const breakpointOffsets = calculateTopPositionsByBreakpoint();
      setTopPosition(calculatedTop);
      setTopOffsetsByBreakpoint(breakpointOffsets);
    };

    updatePosition();
    const timeoutId = setTimeout(updatePosition, 100); // DOM readiness
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isAuthorMode, calculateTopPosition, calculateTopPositionsByBreakpoint]);

  // Reset sticky state and body padding when target content loads (skeleton unmounts)
  useEffect(() => {
    if (isAuthorMode) return;
    if (isTargetContentLoaded) {
      setIsSkeletonSticky(false);
      document.body.style.paddingTop = '';
    }
  }, [isAuthorMode, isTargetContentLoaded]);

  // Scroll handler for skeleton loader sticky behavior (when enableOverlayTop/search-form-position)
  // Skip in author mode - no sticky/skeleton styles needed
  useEffect(() => {
    if (isAuthorMode) return;

    const handleSkeletonScroll = () => {
      if (!skeletonLoaderRef.current || typeof window === 'undefined' || !document.contains(skeletonLoaderRef.current))
        return;

      const rect = skeletonLoaderRef.current.getBoundingClientRect();
      const skeletonTopInDocument = rect.top + window.scrollY;
      const scrollThreshold = skeletonTopInDocument - 1; // Small buffer for when to stick

      if (window.scrollY > scrollThreshold) {
        setIsSkeletonSticky(true);
        // Add body padding to prevent content jump when skeleton becomes sticky
        const skeletonHeight = skeletonLoaderRef.current.offsetHeight;
        if (skeletonHeight > 0) {
          document.body.style.paddingTop = `${skeletonHeight}px`;
        }
      } else {
        setIsSkeletonSticky(false);
        document.body.style.paddingTop = '';
      }
    };

    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleSkeletonScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    handleSkeletonScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      document.body.style.paddingTop = '';
    };
  }, [isAuthorMode]);

  const AuthorComponents = () => {
    return (
      <>
        {Array.from({ length: 1 }, (_, index) => {
          return (
            <div>
              <ResponsiveGrid
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                pagePath={pagePath}
                itemPath={`${itemPath}/${cqItemsOrder[index]}`}
                columnCount="12"
                gridClassNames={''}
                config={{
                  isEmpty: () => true,
                  resourceType: 'mi-aem-common-spa/components/container',
                }}
              />
            </div>
          );
        })}
      </>
    );
  };

  const PublishComponents = () => {
    return (
      <>
        {cqItemsOrder?.map((cardName: string, index: number) => {
          if (Object?.prototype?.hasOwnProperty?.call(cqItems, cardName)) {
            const card = cqItems?.[cardName] as Record<string, unknown>;

            // Check if this is a container component that contains search form components
            const { hasSearchForm, enableOverlayTop } = containerHasSearchForm(card);
            const shouldShowSkeletonLoader = !isTargetContentLoaded && !isAuthorMode && hasSearchForm;

            if (shouldShowSkeletonLoader) {
              // Calculate top position if search form should be positioned on herobanner
              const calculatedTopPosition = enableOverlayTop
                ? topPosition > 0
                  ? topPosition
                  : typeof window !== 'undefined' && window.document
                  ? calculateTopPosition()
                  : 0
                : 0;

              // Sticky top offset when scrolled (0 = stick to viewport top, matching search form behavior)
              const skeletonStickyTop = 0;

              return (
                <StyledSkeletonLoaderFormContainer
                  key={`${cardName}+${index}`}
                  ref={skeletonLoaderRef}
                  className={clsx({
                    'search-form-position': enableOverlayTop,
                  })}
                  topOffset={toRem(calculatedTopPosition)}
                  topOffsetXs={
                    enableOverlayTop && topOffsetsByBreakpoint.xs !== undefined
                      ? toRem(topOffsetsByBreakpoint.xs)
                      : undefined
                  }
                  topOffsetSm={
                    enableOverlayTop && topOffsetsByBreakpoint.sm !== undefined
                      ? toRem(topOffsetsByBreakpoint.sm)
                      : undefined
                  }
                  topOffsetMd={
                    enableOverlayTop && topOffsetsByBreakpoint.md !== undefined
                      ? toRem(topOffsetsByBreakpoint.md)
                      : undefined
                  }
                  topOffsetLg={
                    enableOverlayTop && topOffsetsByBreakpoint.lg !== undefined
                      ? toRem(topOffsetsByBreakpoint.lg)
                      : undefined
                  }
                  isSkeletonSticky={enableOverlayTop && isSkeletonSticky}
                  skeletonStickyTop={toRem(skeletonStickyTop)}
                >
                  <StyledSkeletonLoaderDiv className="container px-0 search-form-wrapper">
                    <div className="search-form-skeleton">
                      <img
                        src="https://cache.marriott.com/content/dam/marriott-digital/cy/us-canada/hws/h/hpnnb/en_us/logo/internal/assets/cy-hpnnb-grey-image-placeholder-34740.png?imbypass=on"
                        alt="loading"
                        className="skeleton-image"
                      />
                    </div>
                  </StyledSkeletonLoaderDiv>
                </StyledSkeletonLoaderFormContainer>
              );
            }

            const gridUtil = respGridUtil({ ...card });
            return (
              <div
                key={`${cardName}+${index}`}
                className={clsx('container-component', {
                  'invisible ':
                    !isTargetContentLoaded && (String(enableOfferVariation) === 'false' || !enableOfferVariation),
                })}
              >
                <ResponsiveGrid {...gridUtil} />
              </div>
            );
          } else return <></>;
        })}
      </>
    );
  };

  // Check if we should show skeleton loader for search form (without wrapper classes)
  const shouldShowSearchFormSkeleton = !isTargetContentLoaded && !isAuthorMode;

  // If showing search form skeleton, render it directly without wrapper
  if (shouldShowSearchFormSkeleton) {
    return <PublishComponents />;
  }

  return <>{isAuthorMode ? <AuthorComponents /> : <PublishComponents />}</>;
};

const ABTestWrapper: FC<INGSearchformABTestWrapperProps> = props => {
  const { mboxParameter } = props;
  const updatedPageModel = useDCAPageModelStore(state => state.pageModel) as IUpdatedPageModel | undefined;

  const [finalModel, setFinalModel] = useState<INGSearchformABTestWrapperProps>(props);
  const processingCompleteRef = useRef(false);

  // Handle target response
  useEffect(() => {
    if (!processingCompleteRef.current) {
      const processedModel = processPhoenixSearchExperience(
        updatedPageModel,
        mboxParameter ?? '',
        props,
        mboxParameter && mboxParameter === 'target-nextGenSearch-mbox' ? false : true
      );
      if (processedModel) {
        setFinalModel(processedModel);
        processingCompleteRef.current = true;
      }
    }
  }, [updatedPageModel, mboxParameter, props]);

  // Reset when props change in author mode
  useEffect(() => {
    if (props.isAuthorMode) {
      setFinalModel(props);
      processingCompleteRef.current = false;
    }
  }, [props]);

  // On first render, use props to let DCA process
  // After processing completes, use finalModel with search form removed
  const modelToRender = processingCompleteRef.current ? finalModel : props;

  return useMemo(() => <DCAContainer {...modelToRender} />, [modelToRender]);
};

const Config = {
  emptyLabel: 'NG Searchform AB Test Wrapper',
  isEmpty: () => true,
  resourceType: `mi-aem-shop-spa/components/content/ngsearchformabtestwrapper`,
};

/**
 * NGSearchFormABTestWrapper Component
 *
 * This wrapper component handles DCA processing and makes a decision to remove
 * either baselinesearchform or ngsearchform from the container items based on the DCA target response.
 *
 * This only affects searchform components within containers, leaving all other components intact.
 *
 * Fallback logic: If there is no target response or if target call fails,
 * it keeps baselinesearchform and removes ngsearchform.
 *
 * This component is specifically designed for homepage usage.
 */
export const NGSearchFormABTestWrapper: FC<INGSearchformABTestWrapperProps> = props => (
  <EditableComponent config={Config} {...props}>
    <ABTestWrapper {...props} />
  </EditableComponent>
);
